import { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { announceCaretaker } from './myVehicles';
import { sessionRole } from '../../authProvider';
import { VARIANT_SCOPE_FEATURE, VARIANT_SCOPE_ROLES } from '../userOffers/variantScope';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  fetchCaretakerPreference,
  saveCaretakerPreference,
  type CaretakerPreference as Preference,
} from './myVehiclesApi';

/**
 * „Jestem opiekunem FTL: …” w menu użytkownika (status 29.09.2026).
 *
 * Każdy wskazuje sam siebie — tak jak sam wybiera śledzone działy
 * w dzwonku powiadomień. Lista pochodzi z kartoteki aut (`Car.ftlCaretaker`
 * z TMS); użytkownik aplikacji nie ma dziś powiązania z tą nazwą.
 * Wskazanie zasila przełącznik „Tylko moje pojazdy” — filtr dla wygody,
 * nie uprawnienie.
 *
 * Natywna lista wyboru, bo rozwijana lista MUI wewnątrz menu użytkownika
 * walczy z nim o klawiaturę i fokus.
 */
/**
 * Podpowiedź pod polem. Dla przełącznika „Tylko moje pojazdy” to filtr
 * widoku; planiście od 07.10.2026 ten sam wybór zawęża też warianty do jego
 * aut (`userOffers/variantScope.ts`), a od 13.10.2026 także wnioski
 * o odpięcie — mówimy to wprost. Bez przełącznika
 * `variantScopeOwnVehicles` (wersja „obecna”, 12.10.2026) — tekst z 0.31.3.
 */
export function caretakerHint(role: string | null | undefined, variantScopeOn = true): string {
  return variantScopeOn && VARIANT_SCOPE_ROLES.includes(role ?? '')
    ? 'Z tego wyboru korzysta przełącznik „Tylko moje pojazdy”, a warianty dodajesz i usuwasz tylko przy autach tego opiekuna — tak samo wniosek o odpięcie. Bez wyboru — bez ograniczenia.'
    : 'Z tego wyboru korzysta przełącznik „Tylko moje pojazdy”. To filtr widoku, nie uprawnienie.';
}

export const CaretakerPreference = () => {
  const [pref, setPref] = useState<Preference | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const variantScopeOn = useFeature(VARIANT_SCOPE_FEATURE);

  useEffect(() => {
    let cancelled = false;
    fetchCaretakerPreference()
      .then((p) => {
        if (!cancelled) setPref(p);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (value: string) => {
    if (!pref) return;
    setSaving(true);
    setError(null);
    try {
      const caretaker = await saveCaretakerPreference(value === '' ? null : value);
      setPref({ ...pref, caretaker });
      announceCaretaker(caretaker);
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    // Klawisze zostają w polu — menu przeskakiwało po literach do pozycji.
    <Box sx={{ px: 2, py: 1, width: 280 }} onKeyDown={(e) => e.stopPropagation()}>
      <TextField
        select
        size="small"
        fullWidth
        label="Jestem opiekunem FTL"
        value={pref?.caretaker ?? ''}
        disabled={pref === null || saving}
        onChange={(e) => void save(e.target.value)}
        slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
      >
        <option value="">— nie ustawiono —</option>
        {/* Wskazany opiekun zostaje na liście, choćby zniknął z kartoteki. */}
        {(pref?.caretaker && !pref.caretakers.includes(pref.caretaker)
          ? [pref.caretaker, ...pref.caretakers]
          : (pref?.caretakers ?? [])
        ).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </TextField>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
        {error ?? caretakerHint(sessionRole(), variantScopeOn)}
      </Typography>
    </Box>
  );
};
